import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import { usePostHog } from "@posthog/react";
import { Auth } from "aws-amplify";
import { logOut } from "components/navigation/menu-bar";
import {
  switchedOrganisationCookieName,
  useIdentity,
} from "contexts/identity-context";
import { Form, Formik, FormikHelpers } from "formik";
import { useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { useIntercom } from "react-use-intercom";
import { object, ref, string } from "yup";

interface ChangePasswordModalProps {
  open: boolean;
  onClose: () => void;
}

interface Values {
  currentPassword: string;
  newPassword: string;
  verifyNewPassword: string;
}

const ChangePasswordModal = ({ open, onClose }: ChangePasswordModalProps) => {
  const [showPasswordCurrent, setShowPasswordCurrent] = useState(false);
  const [showPasswordNew, setShowPasswordNew] = useState(false);
  const [showPasswordVerify, setShowPasswordVerify] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const [identityState, setIdentityState] = useIdentity();
  const { update, shutdown } = useIntercom();
  const posthog = usePostHog();
  const [_c, setCookie, removeCookie] = useCookies([
    switchedOrganisationCookieName,
  ]);
  const [closedown, setClosedown] = useState(5);

  const validationSchema = object().shape({
    currentPassword: string().required("Current password is required"),
    newPassword: string()
      .min(8, "Password must be at least 8 characters")
      .required("New password is required"),
    verifyNewPassword: string()
      .oneOf([ref("newPassword")], "Passwords must match")
      .required("Confirm your new password"),
  });

  useEffect(() => {
    if (closedown > 0) {
      const timer = setTimeout(() => {
        setClosedown((prev) => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (closedown === 0 && successMsg) {
      const logoutGlobally = async () => {
        await logOut(setIdentityState, removeCookie, shutdown, posthog, {
          global: true,
        });
      };
      logoutGlobally();
      handleClose();
    }
  }, [closedown, successMsg]);

  const handleClickShowPasswordCurrent = () =>
    setShowPasswordCurrent(!showPasswordCurrent);
  const handleClickShowPasswordNew = () => setShowPasswordNew(!showPasswordNew);
  const handleClickShowPasswordVerify = () =>
    setShowPasswordVerify(!showPasswordVerify);

  const handleFormSubmit = async (
    values: Values,
    actions: FormikHelpers<Values>
  ) => {
    setErrorMsg("");
    setSuccessMsg("");
    try {
      const user = await Auth.currentAuthenticatedUser();
      await Auth.changePassword(
        user,
        values.currentPassword,
        values.newPassword
      ).then(() => {
        setClosedown(5);
      });
      setSuccessMsg("Password changed successfully.");
      actions.resetForm();
    } catch (err: any) {
      setErrorMsg(err.message || "An error occurred");
    } finally {
      actions.setSubmitting(false);
    }
  };

  const handleClose = () => {
    setErrorMsg("");
    setSuccessMsg("");
    setShowPasswordCurrent(false);
    setShowPasswordNew(false);
    setShowPasswordVerify(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
          handleClose();
        }
      }}
      maxWidth="xs"
      fullWidth
      sx={{
        "& .MuiPaper-root": {
          backgroundImage: "none",
        },
      }}
    >
      <Formik
        initialValues={{
          currentPassword: "",
          newPassword: "",
          verifyNewPassword: "",
        }}
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          isSubmitting,
          isValid,
          dirty,
        }) => (
          <Form>
            <DialogContent sx={{ p: 0 }}>
              <TextField
                label="Current Password"
                name="currentPassword"
                type={showPasswordCurrent ? "text" : "password"}
                fullWidth
                value={values.currentPassword}
                onChange={handleChange}
                onBlur={handleBlur}
                error={
                  touched.currentPassword && Boolean(errors.currentPassword)
                }
                helperText={touched.currentPassword && errors.currentPassword}
                size="small"
                sx={{
                  mt: 1,
                  mb: 1,
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleClickShowPasswordCurrent}>
                        {showPasswordCurrent ? (
                          <VisibilityOffIcon />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="New Password"
                name="newPassword"
                type={showPasswordNew ? "text" : "password"}
                fullWidth
                value={values.newPassword}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.newPassword && Boolean(errors.newPassword)}
                helperText={touched.newPassword && errors.newPassword}
                size="small"
                sx={{
                  mt: 0,
                  mb: 1,
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleClickShowPasswordNew}>
                        {showPasswordNew ? (
                          <VisibilityOffIcon />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Verify New Password"
                name="verifyNewPassword"
                type={showPasswordVerify ? "text" : "password"}
                fullWidth
                value={values.verifyNewPassword}
                onChange={handleChange}
                onBlur={handleBlur}
                error={
                  touched.verifyNewPassword && Boolean(errors.verifyNewPassword)
                }
                helperText={
                  touched.verifyNewPassword && errors.verifyNewPassword
                }
                size="small"
                sx={{
                  mt: 0,
                  mb: 1,
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleClickShowPasswordVerify}>
                        {showPasswordVerify ? (
                          <VisibilityOffIcon />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Alert severity="info" variant="outlined">
                Changing your password will invalidate all logged in sessions.
              </Alert>
              {errorMsg && (
                <Alert severity="error" style={{ marginTop: 10 }}>
                  {errorMsg}
                </Alert>
              )}
              {successMsg && (
                <Alert severity="success" style={{ marginTop: 10 }}>
                  {`${successMsg} You will be logged out in ${closedown} seconds.`}
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleClose}
                size="small"
                disabled={isSubmitting || !!successMsg}
              >
                Cancel
              </Button>
              <Button
                disabled={isSubmitting || !!successMsg || !isValid || !dirty}
                type="submit"
                color="primary"
                variant="contained"
                size="small"
              >
                Change Password
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default ChangePasswordModal;
